import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { SeasonFragment, SeasonLeagueFragment } from '@amf/shared/types/graphql';
import SeasonHeader from '@amf/shared/components/season/SeasonHeader';
import Menu from '@amf/shared/components/menu/Menu';
import Container from '@amf/shared/components/layout/Container';
import OffenseTable from '@amf/shared/components/offense/OffenseTable';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getLanguageLocale } from '@amf/shared/utils/languages';

export interface PageProps extends NavigationProps {
  seasons: SeasonFragment[];
}

export type Props = {
  seasons: SeasonFragment[];
  links: { offenses: string };
};

export default function OffenseListPage({ seasons, links }: Props) {
  const { t } = useTranslate();
  const [season, setSeason] = useState<SeasonFragment>(seasons[0]);
  const [league, setLeague] = useState<SeasonLeagueFragment | undefined>();

  useEffect(() => {
    setSeason(seasons[0]);
    setLeague(seasons[0].leagues[0]);
  }, [seasons]);

  return (
    <>
      <SeasonHeader
        title={t('offense.list.title')}
        seasons={seasons}
        selected={season}
        onSeasonChange={season => {
          setSeason(season);
          setLeague(season.leagues[0]);
        }}
      />
      {league && (
        <>
          <Menu<SeasonLeagueFragment>
            scrollable
            label={t('seasonLeague.label')}
            onChange={({ value }) => setLeague(value)}
            selected={{ id: league.id, title: league.name, value: league }}
            items={season.leagues.map(league => ({
              id: league.id,
              title: league.name,
              value: league,
            }))}
          />
          <Container>
            <OffenseTable
              seasons={seasons}
              league={league}
              prefixes={{ offenses: links.offenses }}
            />
          </Container>
        </>
      )}
    </>
  );
}

export const getServerSideProps =
  (): GetServerSideProps<PageProps> =>
  async ({ locale, res }) => {
    res.setHeader('Cache-Control', 'max-age=0, s-maxage=10');
    const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });
    if (seasons.length === 0) return { notFound: true };

    return {
      props: {
        seasons,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
    };
  };
